import axios from "axios";
import config from "../../config";
import { MAIN_PAGE, GLOBALS } from "@constants";
import { saveState } from "@helpers/storage/storeLocalData";

const privateInstance = axios.create({
  baseURL: config.api_url,
});

export const configurateToken = (token) => {
  privateInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
};

export const removeUserSession = async (navigateTo = MAIN_PAGE) => {
  // Prevent multiple simultaneous logout calls
  if (window.__isLoggingOut) {
    return;
  }
  window.__isLoggingOut = true;

  // Call backend logout API
  try {
    await privateInstance.post("/logout");
  } catch (error) {
    // Continue with logout even if API call fails
    console.error("Logout API error:", error);
  }

  // Clear authToken first
  localStorage.removeItem("authToken");

  // Clear auth from localStorage
  const boState = window.localStorage[GLOBALS.local_state_name];
  if (boState) {
    try {
      const parsedState = JSON.parse(boState);
      saveState(GLOBALS.local_state_name, { ...parsedState, auth: {} });
    } catch (error) {
      // If parsing fails, just clear auth
      saveState(GLOBALS.local_state_name, { auth: {} });
    }
  } else {
    saveState(GLOBALS.local_state_name, { auth: {} });
  }

  // Force immediate redirect using replace (better than href)
  window.location.replace(navigateTo);
};

privateInstance.interceptors.request.use(
  (config) => {
    // Get token from localStorage before each request
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

privateInstance.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    // Don't trigger logout if we're already logging out
    if (window.__isLoggingOut) {
      return Promise.reject(error);
    }

    // Don't trigger logout for the logout endpoint itself
    if (error.config?.url?.includes("/logout")) {
      return Promise.reject(error);
    }

    // Only logout on actual auth failures.
    // Network errors/timeouts/CORS/payload issues may yield no `error.response` and must NOT invalidate session.
    if (error?.response?.status === 401) {
      console.error("401 Unauthorized, logging out:", error);
      removeUserSession();
    }
    return Promise.reject(error);
  },
);

export default privateInstance;
